import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { HEADER_ID, PageLayout } from '@/components/templates/PageLayout';
import {
  CmBaseTable,
  CmBox,
  CmCard,
  CmFlex,
  CmHeadingPagination,
  CmLink,
  CmLongLoading,
  CmMessageCard,
  CmScrollBox,
  CmTableBodyRow,
  CmTd,
  CmTooltip,
  ReactSingleSelectInput,
  SelectForm,
  useCounter,
} from '@/components/uiParts';
import { LIMIT_PER_PAGE } from '@/domain/models/Pagination';
import { DateDomainService } from '@/domain/services';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import { nullToEmptyString } from '@/lib/util';
import { ommisionText } from '@/lib/util';
import { HopeMenuManagementJobNotice } from '@/pages/menu-managements/components/Alert/HopeMenuManagementJobNotice';
import {
  MenuManagementSearchArea,
  SEARCH_AREA_ID,
} from '@/pages/menu-managements/components/SearchAndDrag';
import { optionGroupTableThs } from '@/pages/menu-managements/option-groups/service';
import { MenuManagementOptionGroup } from '@/pages/menu-managements/option-groups/service';
import { routes } from '@/router';
import { useAssociateBrandList } from '@/services/AssociateBrand/hooks/list/useAssociateBrandList';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { useMenuManagementBrandOptionGroupList } from '@/services/BrandOptionGroup/hooks/list/useMenuManagementBrandOptionGroupList';

const MenuManagementOptionGroupsPage: React.FC = (): JSX.Element => {
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404Page('menu_management', history, managerAccountMe);
  const [optionGroups, setOptionGroups] = useState<MenuManagementOptionGroup[]>(
    []
  );
  const [searchCount, setSearchCount] = useState(0);
  const optionGroupLength = optionGroups.length;
  const [associateBrandSelectOptions, setAssociateBrandSelectOptions] =
    useState<SelectForm[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState<number>();

  const [targetAssociateBrandSelect, setTargetAssociateBrandSelect] =
    useState<SelectForm | null>({
      label: '',
      value: '',
    });

  const { count, decrement, increment, customizeCount } = useCounter(1);

  const [titleJa, setTitleJa] = useState<string>('');

  const getListQuery = {
    associateBrandId: targetAssociateBrandSelect?.value ?? '',
    currentPage: 1,
    limit: LIMIT_PER_PAGE,
    title: titleJa,
  } as const;

  const handleInputTitleJa = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitleJa = e.target.value;

    setTitleJa(newTitleJa);
    clearTimeout(Number(timer));
    const newTimer = setTimeout(async () => {
      setIsLoading(true);
      const { brand_option_groups, count } =
        await useMenuManagementBrandOptionGroupList({
          ...getListQuery,
          currentPage: 1,
          title: newTitleJa,
        });
      customizeCount(1);
      setSearchCount(count);
      setOptionGroups(brand_option_groups);
      setIsLoading(false);
    }, 700);
    setTimer(Number(newTimer));
  };

  const associateBrandSelectInput: ReactSingleSelectInput = {
    value: targetAssociateBrandSelect,
    options: associateBrandSelectOptions,
    onChange: async (associateBrand: SelectForm | null) => {
      if (
        associateBrand === null ||
        associateBrand.value === targetAssociateBrandSelect?.value
      )
        return;
      const associateBrandId = associateBrand.value;
      setIsLoading(true);
      setTargetAssociateBrandSelect(associateBrand);
      const { brand_option_groups: option_groups, count } =
        await useMenuManagementBrandOptionGroupList({
          ...getListQuery,
          currentPage: 1,
          associateBrandId,
        });
      customizeCount(1);
      setSearchCount(count);
      setOptionGroups(option_groups);
      setIsLoading(false);
    },
    onReset: () => {
      setTargetAssociateBrandSelect({
        value: '',
        label: '',
      });
    },
  };

  const toFirstPage = async () => {
    setIsLoading(true);
    const { brand_option_groups: option_groups } =
      await useMenuManagementBrandOptionGroupList({
        ...getListQuery,
      });
    setOptionGroups(option_groups);
    customizeCount(1);
    setIsLoading(false);
  };

  const toPreviousPage = async () => {
    setIsLoading(true);
    const previousPage = count - 1;
    const { brand_option_groups: option_groups } =
      await useMenuManagementBrandOptionGroupList({
        ...getListQuery,
        currentPage: previousPage,
      });
    setOptionGroups(option_groups);
    decrement();
    setIsLoading(false);
  };

  const toNextPage = async () => {
    setIsLoading(true);
    const nextPage = count + 1;
    const { brand_option_groups: option_groups } =
      await useMenuManagementBrandOptionGroupList({
        ...getListQuery,
        currentPage: nextPage,
      });
    setOptionGroups(option_groups);
    increment();
    setIsLoading(false);
  };

  const toLastPage = async () => {
    setIsLoading(true);
    const lastPage = Math.ceil(searchCount / LIMIT_PER_PAGE);
    const { brand_option_groups: option_groups } =
      await useMenuManagementBrandOptionGroupList({
        ...getListQuery,
        currentPage: lastPage,
      });
    setOptionGroups(option_groups);
    customizeCount(lastPage);
    setIsLoading(false);
  };

  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, SEARCH_AREA_ID]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const associateBrands = await useAssociateBrandList();
      const associateBrandOptions = associateBrands.map((brand) => {
        return {
          label: brand.name,
          value: brand.id.toString(),
        };
      });
      setAssociateBrandSelectOptions(associateBrandOptions);
      const { associateBrand } = LocalStorageHandler.get('menuManagement');
      const initialAssociateBrand =
        associateBrand !== undefined
          ? associateBrand
          : associateBrandOptions[0];
      LocalStorageHandler.set('menuManagement', {
        associateBrand: initialAssociateBrand,
      });
      const { brand_option_groups: option_groups, count } =
        await useMenuManagementBrandOptionGroupList({
          ...getListQuery,
          associateBrandId: initialAssociateBrand.value,
          currentPage: 1,
        });
      setTargetAssociateBrandSelect(initialAssociateBrand);
      customizeCount(1);
      setSearchCount(count);
      setOptionGroups(option_groups);
      setIsLoading(false);
    })();
  }, []);

  return (
    <PageLayout
      title={routes.menuManagementOptionGroups.title}
      alert={<HopeMenuManagementJobNotice />}
      {...{ associateBrandSelectInput }}
    >
      <CmBox>
        <MenuManagementSearchArea
          targetInputLabel="オプショングループ"
          newPagePath={'/menu-managements/option-groups/new'}
          itemValue={titleJa}
          {...{
            associateBrandSelectInput,
            searchCount,
            handleInputTitleJa,
          }}
        />
        {optionGroupLength > 0 ? (
          <CmCard
            padding="20px"
            height={`calc(100vh - ${targetsHeight}px - 20px)`}
          >
            <CmFlex flexDirection="row-reverse" my="5px">
              <CmHeadingPagination
                totalNum={searchCount}
                currentPage={count}
                LimitPerPage={LIMIT_PER_PAGE}
                {...{ toFirstPage, toNextPage, toPreviousPage, toLastPage }}
              />
            </CmFlex>
            <CmScrollBox>
              <CmBaseTable ths={optionGroupTableThs}>
                {optionGroups.map((optionGroup, index) => {
                  const memo = nullToEmptyString(optionGroup.memo);
                  const titleJa = optionGroup.title.translation.ja;
                  return (
                    <CmTableBodyRow key={index + 1}>
                      <CmTd>
                        <CmLink
                          color="blue.500"
                          to={`/menu-managements/option-groups/${optionGroup.id}/edit`}
                        >
                          <CmTooltip label={titleJa}>
                            {ommisionText(titleJa, 20)}
                          </CmTooltip>
                        </CmLink>
                      </CmTd>
                      <CmTd>
                        <CmTooltip label={memo}>
                          {ommisionText(memo, 20)}
                        </CmTooltip>
                      </CmTd>
                      <CmTd textAlign="center">
                        <CmTooltip label={optionGroup.updated_at}>
                          {DateDomainService.datetimeFormat(
                            new Date(optionGroup.updated_at)
                          )}
                        </CmTooltip>
                      </CmTd>
                    </CmTableBodyRow>
                  );
                })}
              </CmBaseTable>
            </CmScrollBox>
          </CmCard>
        ) : (
          <>
            {!isLoading && (
              <CmMessageCard margin="100px auto 0">
                検索結果に一致するオプショングループが見当たりませんでした。
              </CmMessageCard>
            )}
          </>
        )}
        {isLoading && <CmLongLoading />}
      </CmBox>
    </PageLayout>
  );
};

export default MenuManagementOptionGroupsPage;
